// src/app/shared/interceptors/auth-error.interceptor.ts
import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, EMPTY, throwError } from 'rxjs';
import { catchError, share, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { SingleActionDialogComponent } from '../components/dialogs/single-action-dialog/single-action-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SharedStorageService } from '../services/shared-storage.service';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private isShowingLogoutDialog = false;
  private pendingLogoutRequests = new Set<string>(); // 追蹤待處理的請求

  constructor(
    private injector: Injector, 
    private authService: AuthService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err) => {
        if (!(err instanceof HttpErrorResponse)) {
          return throwError(() => err);
        }

        console.log('error:', err);

        // 先解析 Blob 錯誤,再處理各種 statusCode
        return this.handleBlobError(err).pipe(
          switchMap((errorBody) => {
            // 檢查是否為 token 過期錯誤
            if (errorBody?.statusCode === 'EC6008') {
              console.log('EC6008，重新取得有效token');
              
              return this.authService.getValidToken().pipe(
                switchMap((authResponse) => {
                  if (!authResponse?.data?.accessToken) {
                    console.log('無法取得新 token，執行登出');
                    const auth = this.injector.get(AuthService);
                    const router = this.injector.get(Router);
                    const sharedStorageService = this.injector.get(SharedStorageService);
                    auth.clearToken();
                    sharedStorageService.clear();
                    sharedStorageService.remove('fromIdLogin');
                    sharedStorageService.remove('myReservation');
                    sharedStorageService.remove('savedQuestionnaireData');
                    router.navigate([''], { replaceUrl: true });
                    return EMPTY;
                  }

                  console.log("new token:", authResponse.data.accessToken);
                  this.authService.setToken(authResponse.data.accessToken);

                  const newAuthReq = req.clone({
                    setHeaders: { Authorization: `Bearer ${authResponse.data.accessToken}` }
                  });
                  return next.handle(newAuthReq);
                }),
                catchError(() => {
                  const auth = this.injector.get(AuthService);
                  const router = this.injector.get(Router);
                  auth.performLogout().subscribe({
                    next: (res) => {
                      auth.clearToken();
                      console.log('登出 API 成功:', res);
                    },
                    error: (error) => {
                      auth.clearToken();
                      console.error('登出 API 失敗:', error);
                    }
                  });
                  router.navigate([''], { replaceUrl: true });
                  return EMPTY;
                })
              );
            }

            // 已在其他裝置登入(異地登入被登出)
            if (errorBody?.statusCode === 'EC6010' || errorBody?.errorMessage === '已在其他裝置登入') {
              const translateService = this.injector.get(TranslateService);
              const sharedStorageService = this.injector.get(SharedStorageService);
              const dialog = this.injector.get(MatDialog);
              const auth = this.injector.get(AuthService);
              const router = this.injector.get(Router);

              dialog.closeAll();
              auth.clearToken();
              sharedStorageService.clear();
              sharedStorageService.remove('fromIdLogin');
              sharedStorageService.remove('myReservation');
              sharedStorageService.remove('savedQuestionnaireData');
              router.navigate([''], { replaceUrl: true });

              const hint = dialog.open(SingleActionDialogComponent, {
                disableClose: true,
                data: {
                  title: translateService.instant('otp-dialog.single-action-title'),
                  message: translateService.instant('otp-dialog.single-action-message'),
                  showCloseButton: false,
                  buttonText: translateService.instant('otp-dialog.single-action-button-text'),
                  action: () => hint.close(),
                },
              });
              return EMPTY;
            }

            // 已在其他地方登入
            if (errorBody?.statusCode === 'E9028') {
              const auth = this.injector.get(AuthService);
              const router = this.injector.get(Router);
              const sharedStorageService = this.injector.get(SharedStorageService);
              sharedStorageService.clear();
              sharedStorageService.remove('fromIdLogin');
              sharedStorageService.remove('myReservation');
              sharedStorageService.remove('savedQuestionnaireData');
              auth.clearToken();
              router.navigate(['/healthcheck/idlogin'], { replaceUrl: true });
              return EMPTY;
            }

            // 401 沒帶 token or 無效 or 過期
            if (err.status === 401 && (errorBody?.statusCode === 'EC6009' || errorBody?.statusCode === 'EC6007')) {
              const requestKey = `${req.method}-${req.url}`;
              
              if (this.pendingLogoutRequests.has(requestKey)) {
                return EMPTY;
              }
              
              if (this.isShowingLogoutDialog) {
                return EMPTY;
              }

              this.pendingLogoutRequests.add(requestKey);
              
              const auth = this.injector.get(AuthService);
              const router = this.injector.get(Router);
              const dialog = this.injector.get(MatDialog);
              const translateService = this.injector.get(TranslateService);
              const sharedStorageService = this.injector.get(SharedStorageService);

              router.navigate([''], { replaceUrl: true });

              translateService.get([
                'login-register.automatically-logout.title',
                'login-register.automatically-logout.message',
                'common.button.i-understood'
              ]).subscribe(translations => {
                const fromIdLogin = sharedStorageService.get('fromIdLogin');
                
                if (fromIdLogin === true) {
                  console.log('在 idLogin 頁面，不顯示彈窗，背景清理並重新載入');
                  sharedStorageService.clear();
                  sharedStorageService.remove('fromIdLogin');
                  sharedStorageService.remove('myReservation');
                  sharedStorageService.remove('savedQuestionnaireData');
                  auth.clearToken();
                  this.pendingLogoutRequests.clear();
                  return;
                }

                this.isShowingLogoutDialog = true;

                const hint = dialog.open(SingleActionDialogComponent, {
                  disableClose: true,
                  data: {
                    title: translations['login-register.automatically-logout.title'],
                    message: translations['login-register.automatically-logout.message'],
                    showCloseButton: false,
                    buttonText: translations['common.button.i-understood'],
                    action: () => {
                      sharedStorageService.clear();
                      sharedStorageService.remove('fromIdLogin');
                      sharedStorageService.remove('myReservation');
                      sharedStorageService.remove('savedQuestionnaireData');
                      auth.clearToken();
                      hint.close();
                    },
                  },
                });

                hint.afterClosed().subscribe(() => {
                  this.isShowingLogoutDialog = false;
                  this.pendingLogoutRequests.clear();
                  window.location.href = '/healthcheck';
                });
              });

              return EMPTY;
            }

            return throwError(() => err);
          })
        );
      })
    );
  }

  // 處理 Blob 格式的錯誤回應
  private handleBlobError(err: HttpErrorResponse): Observable<any> {
    if (err.error instanceof Blob && err.error.type === 'application/json') {
      return new Observable(observer => {
        err.error.text().then((text: any) => {
          try {
            const errorBody = JSON.parse(text);
            observer.next(errorBody);
            observer.complete();
          } catch (parseError) {
            console.error('解析 Blob 錯誤失敗:', parseError);
            observer.next(err.error);
            observer.complete();
          }
        });
      });
    }
    return new Observable(observer => {
      observer.next(err.error);
      observer.complete();
    });
  }
}
